import { API_BASE_URL } from "../apiConfig";
import React, { useCallback, useState, useEffect } from "react";
import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";

import "./AddComplaint.css";
import "leaflet/dist/leaflet.css";

// Fix Leaflet Default Icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
});

const DEFAULT_LOCATION = { lat: 33.62000, lng: 73.05000 };
const PAKISTAN_BOUNDS = [
  [23.5, 60.8],
  [37.2, 77.1]
];

const isWithinPakistan = ({ lat, lng }) => (
  lat >= PAKISTAN_BOUNDS[0][0] &&
  lat <= PAKISTAN_BOUNDS[1][0] &&
  lng >= PAKISTAN_BOUNDS[0][1] &&
  lng <= PAKISTAN_BOUNDS[1][1]
);

const formatPlaceName = (place) => {
  const address = place.address || {};
  return [
    place.name,
    address.suburb || address.neighbourhood || address.town || address.city || address.village,
    address.state,
    "Pakistan"
  ].filter(Boolean).filter((value, index, self) => self.indexOf(value) === index).join(", ");
};

const searchPakistanLocations = async (query, { limit = 5, signal } = {}) => {
  const params = new URLSearchParams({
    q: query,
    format: "json",
    addressdetails: "1",
    countrycodes: "pk",
    limit: String(limit)
  });

  const res = await fetch(`https://nominatim.openstreetmap.org/search?${params.toString()}`, { signal });
  return res.json();
};

const LocationMarker = ({ marker, setMarker, showNotification }) => {
  useMapEvents({
    click(e) {
      const nextMarker = { lat: e.latlng.lat, lng: e.latlng.lng };
      if (isWithinPakistan(nextMarker)) {
        setMarker(nextMarker);
      } else {
        showNotification("Please select a location inside Pakistan", "error");
      }
    },
  });

  return marker ? (
    <Marker
      position={marker}
      draggable
      eventHandlers={{
        dragend: (e) => {
          const nextMarker = e.target.getLatLng();
          const nextPosition = { lat: nextMarker.lat, lng: nextMarker.lng };
          if (isWithinPakistan(nextPosition)) {
            setMarker(nextPosition);
          } else {
            e.target.setLatLng(marker);
            showNotification("Please select a location inside Pakistan", "error");
          }
        },
      }}
    />
  ) : null;
};

const MapUpdater = ({ marker }) => {
  const map = useMap();

  useEffect(() => {
    if (marker) {
      map.setView([marker.lat, marker.lng], Math.max(map.getZoom(), 14));
    }
  }, [map, marker]);

  return null;
};

const AddComplaint = ({ usernameProp }) => {
  const [image, setImage] = useState(null);
  const [description, setDescription] = useState("");
  const [marker, setMarker] = useState(DEFAULT_LOCATION);
  const [locationSearch, setLocationSearch] = useState("");
  const [locationSuggestions, setLocationSuggestions] = useState([]);
  const [showLocationSuggestions, setShowLocationSuggestions] = useState(false);
  const [isSearchingLocation, setIsSearchingLocation] = useState(false);
  const [isFetchingCurrentLocation, setIsFetchingCurrentLocation] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("success");
  const [showMessage, setShowMessage] = useState(false);
  const [username, setUsername] = useState("guest");

  const showNotification = useCallback((text, type) => {
    setMessage(text);
    setMessageType(type);
    setShowMessage(true);
    setTimeout(() => setShowMessage(false), 2500);
  }, []);

  const fetchCurrentLocation = useCallback(({ silent = false } = {}) => {
    if (!navigator.geolocation) {
      if (!silent) showNotification("Current location is not supported on this browser", "error");
      return;
    }

    setIsFetchingCurrentLocation(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const currentMarker = {
          lat: position.coords.latitude,
          lng: position.coords.longitude
        };

        setIsFetchingCurrentLocation(false);
        if (!isWithinPakistan(currentMarker)) {
          if (!silent) showNotification("Current location must be inside Pakistan", "error");
          return;
        }

        setMarker(currentMarker);
        if (!silent) showNotification("Current location selected", "success");
      },
      (err) => {
        console.error("Failed to fetch current location:", err);
        setIsFetchingCurrentLocation(false);
        if (!silent) showNotification("Please allow location access or search manually", "error");
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 60000
      }
    );
  }, [showNotification]);

  useEffect(() => {
    setIsMobile(/Mobi|Android/i.test(navigator.userAgent));

    if (usernameProp) {
      setUsername(usernameProp);
    } else {
      const sessionStored = sessionStorage.getItem("currentUser");
      if (sessionStored) {
        try {
          const userData = JSON.parse(sessionStored);
          if (userData?.role === "user" && userData.username) {
            setUsername(userData.username);
            return;
          }
        } catch (err) {
          console.error("Failed to parse session user:", err);
        }
      }

      const storedUser = localStorage.getItem("user");
      if (storedUser) {
        try {
          const userData = JSON.parse(storedUser);
          if (userData?.username) setUsername(userData.username);
        } catch (err) {
          setUsername("guest");
        }
      }
    }
  }, [usernameProp]);

  useEffect(() => {
    fetchCurrentLocation({ silent: true });
  }, [fetchCurrentLocation]);

  useEffect(() => {
    const query = locationSearch.trim();
    if (query.length < 2) {
      setLocationSuggestions([]);
      setShowLocationSuggestions(false);
      return;
    }

    const controller = new AbortController();
    const timeout = setTimeout(async () => {
      setIsSearchingLocation(true);
      setShowLocationSuggestions(true);

      try {
        const results = await searchPakistanLocations(query, {
          limit: 5,
          signal: controller.signal
        });

        setLocationSuggestions(Array.isArray(results) ? results : []);
        setShowLocationSuggestions(true);
      } catch (err) {
        if (err.name !== "AbortError") {
          console.error("Location suggestions failed:", err);
        }
      } finally {
        setIsSearchingLocation(false);
      }
    }, 350);

    return () => {
      clearTimeout(timeout);
      controller.abort();
    };
  }, [locationSearch]);

  const selectLocationSuggestion = (place) => {
    const nextMarker = {
      lat: Number(place.lat),
      lng: Number(place.lon)
    };

    if (!isWithinPakistan(nextMarker)) {
      showNotification("Please select a location inside Pakistan", "error");
      return;
    }

    setMarker(nextMarker);
    setLocationSearch(formatPlaceName(place) || place.display_name);
    setLocationSuggestions([]);
    setShowLocationSuggestions(false);
  };

  const handleLocationSearch = async (e) => {
    e.preventDefault();

    const query = locationSearch.trim();
    if (!query) {
      showNotification("Please enter a location to search", "error");
      return;
    }

    setIsSearchingLocation(true);

    try {
      const results = await searchPakistanLocations(query, { limit: 1 });

      if (!Array.isArray(results) || results.length === 0) {
        showNotification("Location not found in Pakistan", "error");
        return;
      }

      selectLocationSuggestion(results[0]);
      showNotification("Location selected", "success");
    } catch (err) {
      console.error("Location search failed:", err);
      showNotification("Failed to search location", "error");
    } finally {
      setIsSearchingLocation(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!image && description.trim() === "") {
      showNotification("Please upload an image or write a description", "error");
      return;
    }

    const formData = new FormData();
    formData.append("description", description);
    formData.append("lat", marker.lat);
    formData.append("lng", marker.lng);
    formData.append("username", username);
    if (image) formData.append("image", image);

    try {
      const res = await fetch(`${API_BASE_URL}/api/complaint/add-complaint`, {
        method: "POST",
        body: formData,
      });

      const data = await res.json();
      showNotification(data.message || "Complaint submitted successfully", "success");

      // Reset form
      setImage(null);
      setDescription("");
      setMarker(DEFAULT_LOCATION);
      setLocationSearch("");
      setLocationSuggestions([]);
      setShowLocationSuggestions(false);
    } catch (err) {
      console.error(err);
      showNotification("Failed to submit complaint", "error");
    }
  };

  return (
    <div className="add-complaint-container">
      <div className="complaint-header">
        <span className="plus-icon">+</span>
        <h2>Add Complaint</h2>
      </div>
      <p className="complaint-subtitle">Report waste or cleanliness issues in your area</p>

      {/* Upload Image Section */}
      <div className="upload-section">
        <label className="section-label">Upload Image</label>
        <div className="upload-options">
          <label htmlFor="galleryUpload" className="upload-option">
            <div className="icon-circle">
              <img src="https://cdn-icons-png.flaticon.com/512/747/747376.png" alt="Gallery" />
            </div>
            <p>Gallery</p>
            <small>Choose from gallery</small>
          </label>

          <label htmlFor="cameraUpload" className="upload-option">
            <div className="icon-circle">
              <img src="https://cdn-icons-png.flaticon.com/512/685/685655.png" alt="Camera" />
            </div>
            <p>Camera</p>
            <small>Take a new photo</small>
          </label>
        </div>

        <input
          type="file"
          id="galleryUpload"
          accept="image/*"
          style={{ display: "none" }}
          onChange={(e) => setImage(e.target.files[0])}
        />
        <input
          type="file"
          id="cameraUpload"
          accept="image/*"
          capture={isMobile ? "environment" : undefined}
          style={{ display: "none" }}
          onChange={(e) => setImage(e.target.files[0])}
        />

        <div className="file-name">
          {image ? image.name : "No file chosen"}
        </div>
      </div>

      {/* Location Section */}
      <div className="location-section">
        <label className="section-label">Location</label>

        <form className="location-search" onSubmit={handleLocationSearch}>
          <div className="location-search-field">
            <input
              type="text"
              placeholder="Search places in Pakistan"
              value={locationSearch}
              onChange={(e) => setLocationSearch(e.target.value)}
              onFocus={() => {
                if (locationSuggestions.length > 0) setShowLocationSuggestions(true);
              }}
              onBlur={() => setTimeout(() => setShowLocationSuggestions(false), 150)}
              autoComplete="off"
            />
            {showLocationSuggestions && (
              <div className="location-suggestions">
                {locationSuggestions.length > 0 ? (
                  locationSuggestions.map((place) => (
                    <button
                      type="button"
                      className="location-suggestion"
                      key={place.place_id}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => selectLocationSuggestion(place)}
                    >
                      <span className="suggestion-title">{formatPlaceName(place) || place.display_name}</span>
                      <span className="suggestion-meta">Pakistan</span>
                    </button>
                  ))
                ) : (
                  <div className="location-suggestion-empty">
                    {isSearchingLocation ? "Searching Pakistan..." : "No Pakistan location found"}
                  </div>
                )}
              </div>
            )}
          </div>
          <button type="submit" disabled={isSearchingLocation}>
            {isSearchingLocation ? "Searching..." : "Search"}
          </button>
        </form>
        
        <div className="location-input">
          <span className="location-pin">📍</span>
          <input
            type="text"
            value={`Lat: ${marker.lat.toFixed(5)}, Lng: ${marker.lng.toFixed(5)}`}
            readOnly
          />
        </div>

        <div className="map-container">
          <button
            type="button"
            className="map-current-location-btn"
            onClick={() => fetchCurrentLocation()}
            disabled={isFetchingCurrentLocation}
            title="Use current location"
            aria-label="Use current location"
          >
            {isFetchingCurrentLocation ? "..." : "GPS"}
          </button>
          <MapContainer
            center={[DEFAULT_LOCATION.lat, DEFAULT_LOCATION.lng]}
            zoom={6}
            minZoom={5}
            maxBounds={PAKISTAN_BOUNDS}
            maxBoundsViscosity={1}
            style={{ height: "220px", width: "100%" }}
          >
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution="&copy; OpenStreetMap"
            />
            <LocationMarker marker={marker} setMarker={setMarker} showNotification={showNotification} />
            <MapUpdater marker={marker} />
          </MapContainer>
          <small className="map-hint">Search Pakistan, use current location, or click/drag the marker to adjust location</small>
        </div>
      </div>

      {/* Description Section */}
      <div className="description-section">
        <label className="section-label">Description</label>
        <textarea
          placeholder="Describe Complaint Here..."
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows="4"
        />
      </div>

      {/* Submit Button with Arrow */}
     <button className="submit-btn" onClick={handleSubmit}>
  <span className="arrow-icon">➤</span>
  <span className="btn-text">Submit</span>

</button>

      {/* Notification Message */}
      {showMessage && (
        <div className={`notification ${messageType}`}>
          {message}
        </div>
      )}
    </div>
  );
};

export default AddComplaint;
